import TemsPolicy from './security/TemsPolicy';
import AuthorizationView from './security/AuthorizationView';

type GatewayOptions = {
    processModal?: { title: string; content?: string }
    formatter?: 'text'|'json'
};

type DataJsonType = { [key: string]: any };
type DataTextType = string;
type DataType = DataJsonType | DataTextType;

export default class TemsGateway {
    public static readonly formatters = {
        'text': { mime: 'text/plain', transformer: (a) => a, default: "" },
        'json': { mime: 'application/json', transformer: (a) => JSON.stringify(a), default: {} }
    }
    private authorizationView: AuthorizationView;
    public constructor(authorizationView: AuthorizationView) {
        this.authorizationView = authorizationView;
    }

    public get(url: string, parameters: DataJsonType = {}, policies?: TemsPolicy[], options?: GatewayOptions): Promise<Response> {
        var encoded = this.urlEncode(parameters);
        var requestUri = url + ((encoded.length === 0) ? '' : '?' + encoded);
        return this.fetch(requestUri, {}, policies);
    }

    public getJson<T>(url: string, parameters: DataJsonType = {}, policies?: TemsPolicy[]) : Promise<T>{
        return this.fetchJson<T, DataJsonType>('get', url, parameters, policies);
    }

    public head(url: string, policies: TemsPolicy[]) {
        return this.fetch(url, {
            method: 'head'
        }, policies);
    }

    public put(url: string, parameters: DataType, policies?: TemsPolicy[], options?: GatewayOptions) {
        return this.fetch(url, this.BuildDataRequest('put', parameters, options), policies);
    }

    public putJson<T>(url: string, parameters?: DataType, policies?: TemsPolicy[], options?: GatewayOptions) {
        return this.fetchJson<T, DataType>('put', url, parameters, policies, options);
    }

    public post(url: string, parameters: DataType, policies?: TemsPolicy[], options?: GatewayOptions) {
        return this.fetch(url, this.BuildDataRequest('post', parameters, options), policies);
    }

    private BuildDataRequest(method: 'put' | 'post', parameters: DataType, options?: GatewayOptions): RequestInit {
        var formatter = TemsGateway.formatters[(options && options.formatter) ? options.formatter : 'json'];
        return {
            method: method,
            headers: {
                "Content-Type": formatter.mime
            },
            body: formatter.transformer(parameters)
        }
    }

    public postJson<T>(url: string, parameters: DataType = {}, policies?: TemsPolicy[], options?: GatewayOptions): Promise<T> {
        return this.fetchJson<T, DataType>('post', url, parameters, policies, options);
    }

    public delete(url: string, policies?: TemsPolicy[]) {
        return this.fetch(url, {
            method: 'delete'
        }, policies);
    }

    private fetch(url: string, init: RequestInit, policies: TemsPolicy[]) {
        if (policies && policies.length) {
            return this.authorizationView.getTokenForPolicy(...policies).then((token) => {
                init.headers = init.headers || {};
                init.headers['Authorization'] = 'Bearer ' + token.token;
                return fetch(url, init);
            });
        }

        return fetch(url, init);
    }

    private fetchJson<T, TParam>(method: 'put' | 'post' | 'get', url: string, parameters: TParam, policies: TemsPolicy[], options?: GatewayOptions) : Promise<T> {
        return (<any>this[method])(url, parameters, policies, options).then(response => {
            if (response.ok) {
                return response.json();
            }
            return Promise.reject(response);
        });
    }

    private urlEncode(parameters: { [key: string]: any } = {}): string {
        return Object.keys(parameters).map(function (key) {
            return key + '=' + encodeURI(parameters[key])
        }).join("&");
    }
}